import { Fragment, useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  canUseAdminSeedPreview,
  fetchLatestDropForCreator,
  loadAdminSeedPreview,
  normalizeCreator,
} from "../utils/homeSeedPreview";
import {
  fetchClosingSoonDrops,
  fetchFollowingFeedDrops,
  isOpenFeedDrop,
} from "../services/dropsFeedService";
import { fetchCreators } from "../services/userQueryService";
import { selectFollowedClosingSoon } from "../utils/followedClosingSoon";
import { pickHomeFeaturedFromFeed } from "../utils/revolvingFeatured";
import useLastVisit from "../hooks/useLastVisit";
import useCreatorRecap from "../hooks/useCreatorRecap";
import FeaturedCard from "../components/featured/FeaturedCard";
import ProfileAvatarLink from "../components/profile/ProfileAvatarLink";
import ProfileNameLink from "../components/profile/ProfileNameLink";
import {
  getNicheMeta,
  NicheBadge,
  NicheBrowseTile,
  NicheInline,
  NICHE_BROWSE_LIST,
} from "../components/niche";
import SessionDismissible, { NUDGE_IDS } from "../components/nudges/SessionDismissible";
import FeedReferralPill from "../components/referral/FeedReferralPill";
import "./Home.css";

/* ─────────────────────────────────────────────
   ICONS — hand-coded inline SVGs only, no icon
   libraries, per design system rules.
───────────────────────────────────────────── */

function IconHeart({ filled, ...props }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  );
}
function IconBookmark(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
    </svg>
  );
}
function IconShare(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
function IconWarning(props) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
function IconLock(props) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}
function IconChevron(props) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */

function viewsLeft(drop) {
  return Math.max((drop.maxViews || 100) - (drop.viewCount || 0), 0);
}

function pctViewed(drop) {
  const max = drop.maxViews || 100;
  return Math.min(100, Math.round(((drop.viewCount || 0) / max) * 100));
}

function timeSince(ts) {
  if (!ts?.seconds) return "";
  const diff = Date.now() - ts.seconds * 1000;
  if (diff < 3600000) return `${Math.max(1, Math.floor(diff / 60000))}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function niche(drop) {
  return getNicheMeta(drop?.niche);
}

function formatPrice(n) {
  if (typeof n !== "number") return "—";
  return n.toFixed(2).replace(/\.00$/, "");
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  return 0;
}

/**
 * Resolves which of the three access states a drop is in for the current fan:
 *  - free           anyone can watch, no unlock needed
 *  - paid-locked    one-time price, not yet purchased
 *  - paid-unlocked  one-time price, already purchased
 *  - sub-locked     behind a subscription tier, fan isn't subscribed
 *  - sub-unlocked   behind a subscription tier, fan is subscribed
 *
 * ASSUMPTION: drop.accessType is one of "free" | "paid" | "subscription".
 * ASSUMPTION: purchasedDropIds / subscribedCreatorIds come from the fan's
 * user doc — confirm the actual field names against Firestore.
 */
function getDropAccess(drop, purchasedDropIds, subscribedCreatorIds) {
  if (drop.accessType === "free" || drop.price === 0) {
    return { type: "free" };
  }
  if (drop.accessType === "paid") {
    return purchasedDropIds.has(drop.id) ? { type: "paid-unlocked" } : { type: "paid-locked" };
  }
  if (drop.accessType === "subscription") {
    return subscribedCreatorIds.has(drop.creatorId) ? { type: "sub-unlocked" } : { type: "sub-locked" };
  }
  return { type: "free" };
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */

export default function Home() {
  const { currentUser, userProfile } = useAuth();
  const { previousVisitAt } = useLastVisit(currentUser);
  const { recap: creatorRecap } = useCreatorRecap(currentUser, userProfile?.isCreator, previousVisitAt);

  const [loading, setLoading] = useState(true);
  const [followingRows, setFollowingRows] = useState([]); // sidebar: { creator, drop|null } per creator
  const [feedRows, setFeedRows] = useState([]); // main feed: all open drops from followed creators
  const [closingFast, setClosingFast] = useState([]);
  const [totalCreators, setTotalCreators] = useState(null);
  const [purchasedDropIds, setPurchasedDropIds] = useState(new Set());
  const [subscribedCreatorIds, setSubscribedCreatorIds] = useState(new Set());
  const [firstViewRanks, setFirstViewRanks] = useState({}); // { [dropId]: rank }
  const [myDisplayName, setMyDisplayName] = useState(null);
  const [seedPreviewActive, setSeedPreviewActive] = useState(false);
  const heroRef = useRef(null);
  const [sidebarsPinned, setSidebarsPinned] = useState(false);

  // Collapsible "Closing soon" / "Open now" sections — default collapsed
  // on mobile (where the sidebar stacks above the feed and a long
  // creator list pushes the actual feed way down the page), default
  // expanded on desktop (where the sidebar is its own pinned column
  // and never blocks the feed in the first place). Lazy initializer
  // so the correct default renders on first paint, no flash.
  const [closingExpanded, setClosingExpanded] = useState(
    () => typeof window === "undefined" || !window.matchMedia("(max-width: 880px)").matches
  );
  const [openExpanded, setOpenExpanded] = useState(
    () => typeof window === "undefined" || !window.matchMedia("(max-width: 880px)").matches
  );

  const updateSidebarPin = useCallback(() => {
    const hero = heroRef.current;
    if (!hero) return;

    if (window.matchMedia("(max-width: 880px)").matches) {
      setSidebarsPinned(false);
      return;
    }

    const page = hero.closest(".hm-page");
    const navOffset = page
      ? parseFloat(getComputedStyle(page).getPropertyValue("--hm-nav-offset")) || 64
      : 64;
    const heroRect = hero.getBoundingClientRect();
    setSidebarsPinned(heroRect.bottom <= navOffset);
  }, []);

  useEffect(() => {
    updateSidebarPin();
    window.addEventListener("scroll", updateSidebarPin, { passive: true });
    window.addEventListener("resize", updateSidebarPin);
    return () => {
      window.removeEventListener("scroll", updateSidebarPin);
      window.removeEventListener("resize", updateSidebarPin);
    };
  }, [updateSidebarPin, feedRows.length, seedPreviewActive, loading]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setSeedPreviewActive(false);
      try {
        // ── Total Canadian creators (for nav/live stats) ──
        const creators = await fetchCreators(100);
        if (!cancelled) setTotalCreators(creators.length);

        // ── Following list + access data ──
        let followingIds = [];
        let purchased = [];
        let subscriptions = [];
        let previewSourceUid = null;
        let usingSeedPreview = false;

        if (currentUser) {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          const userData = userDoc.exists() ? userDoc.data() : {};
          followingIds = userData.following || [];
          purchased = userData.purchasedDrops || [];
          subscriptions = userData.subscriptions || [];

          if (
            canUseAdminSeedPreview(userProfile) &&
            followingIds.length === 0
          ) {
            const preview = await loadAdminSeedPreview();
            followingIds = preview.following;
            purchased = preview.purchasedDrops;
            subscriptions = preview.subscriptions;
            previewSourceUid = preview.previewSourceUid;
            usingSeedPreview = followingIds.length > 0;
            if (!cancelled && usingSeedPreview) {
              setSeedPreviewActive(true);
            }
          }

          if (!cancelled) {
            setPurchasedDropIds(new Set(purchased));
            setSubscribedCreatorIds(new Set(subscriptions));
            setMyDisplayName(userData.displayName || currentUser.displayName || null);
          }
        }

        const rows = await Promise.all(
          followingIds.map(async (creatorId) => {
            const creatorDoc = await getDoc(doc(db, "users", creatorId));
            if (!creatorDoc.exists()) return null;
            const creator = normalizeCreator(creatorId, creatorDoc.data());
            const drop = await fetchLatestDropForCreator(creatorId);
            return { creator, drop };
          })
        );

        const cleanRows = rows.filter(Boolean);
        if (!cancelled) setFollowingRows(cleanRows);

        const feedDrops = await fetchFollowingFeedDrops(followingIds, 50);
        const feedWithCreators = await Promise.all(
          feedDrops.map(async (drop) => {
            const creatorDoc = await getDoc(doc(db, "users", drop.creatorId));
            const creator = creatorDoc.exists()
              ? normalizeCreator(drop.creatorId, creatorDoc.data())
              : normalizeCreator(drop.creatorId, { displayName: "Creator", avatarUrl: "" });
            return { creator, drop };
          })
        );
        if (!cancelled) setFeedRows(feedWithCreators);

        // ── First-view ranks for closed drops ──
        const rankUserId =
          usingSeedPreview && previewSourceUid
            ? previewSourceUid
            : currentUser?.uid;

        if (rankUserId) {
          const closedDrops = cleanRows
            .filter((r) => r.drop && !isOpenFeedDrop(r.drop))
            .map((r) => r.drop);

          if (closedDrops.length > 0) {
            const rankEntries = await Promise.all(
              closedDrops.map(async (d) => {
                const fvDoc = await getDoc(doc(db, "first_views", `${d.id}_${rankUserId}`));
                return fvDoc.exists() ? [d.id, fvDoc.data().rank] : null;
              })
            );
            if (!cancelled) {
              setFirstViewRanks(Object.fromEntries(rankEntries.filter(Boolean)));
            }
          }
        }

        // ── Closing fast — platform-wide open drops, fewest views left first ──
        const sorted = await fetchClosingSoonDrops(50);

        const withCreators = await Promise.all(
          sorted.map(async (d) => {
            const creatorDoc = await getDoc(doc(db, "users", d.creatorId));
            return {
              drop: d,
              creator: creatorDoc.exists()
                ? normalizeCreator(d.creatorId, creatorDoc.data())
                : { displayName: "Creator", avatarUrl: "" },
            };
          })
        );
        if (!cancelled) setClosingFast(withCreators);
      } catch (err) {
        console.error("Home load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [currentUser, userProfile]);

  const urgentFeedRows = useMemo(
    () => feedRows.filter((r) => isOpenFeedDrop(r.drop) && viewsLeft(r.drop) <= 15),
    [feedRows]
  );
  const openFeedRows = useMemo(
    () => feedRows.filter((r) => isOpenFeedDrop(r.drop) && viewsLeft(r.drop) > 15),
    [feedRows]
  );
  const urgentSidebarRows = useMemo(
    () => selectFollowedClosingSoon(followingRows),
    [followingRows]
  );
  const openSidebarRows = useMemo(
    () => followingRows.filter((r) => r.drop && isOpenFeedDrop(r.drop) && viewsLeft(r.drop) > 15),
    [followingRows]
  );
  const seenRows = useMemo(
    () => followingRows.filter((r) => !r.drop || !isOpenFeedDrop(r.drop)),
    [followingRows]
  );

  const featuredSlide = useMemo(
    () => pickHomeFeaturedFromFeed(feedRows, currentUser?.uid),
    [feedRows, currentUser?.uid]
  );

  const followingIds = useMemo(
    () => followingRows.map((r) => r.creator?.id).filter(Boolean),
    [followingRows]
  );

  const closingFastUnfollowed = useMemo(() => {
    const followed = new Set(followingIds);
    return closingFast.filter((item) => {
      const creatorId = item.drop?.creatorId;
      if (!creatorId) return false;
      if (currentUser?.uid && creatorId === currentUser.uid) return false;
      return !followed.has(creatorId);
    });
  }, [closingFast, followingIds, currentUser?.uid]);

  const openFeedList = useMemo(
    () => [...urgentFeedRows, ...openFeedRows],
    [urgentFeedRows, openFeedRows]
  );

  // ── "New since you were last here" — the fan-facing honest
  // freshness marker. Only meaningful once there's a real previous
  // visit to compare against; a first-ever visit shows nothing here,
  // since everything trivially being "new" isn't a useful signal.
  const previousVisitMillis = toMillis(previousVisitAt);
  const newSinceLastVisit = useMemo(() => {
    if (!previousVisitMillis) return 0;
    return openFeedList.filter((r) => toMillis(r.drop.createdAt) > previousVisitMillis).length;
  }, [openFeedList, previousVisitMillis]);

  const greeting = getGreeting();

  return (
    <div className="hm-page">
      {/* Nav is rendered globally by the shared <Nav /> component in App.js —
          do not add a page-level nav here. */}

      {seedPreviewActive && (
        <div className="hm-seed-preview-banner">
          Admin preview — showing seed data from demo_fan_001 (jordanmtl). Log out and back in to persist following on your profile.
        </div>
      )}

      {/* ── HERO — shared FeaturedCard (Gallery treatment), followed open drops ── */}
      <div ref={heroRef} className="hm-hero-wrap">
        {!loading && featuredSlide && (
          <FeaturedCard
            variant="home"
            creator={featuredSlide.creator}
            drop={featuredSlide.drop}
            access={getDropAccess(
              featuredSlide.drop,
              purchasedDropIds,
              subscribedCreatorIds
            )}
            followingIds={followingIds}
          />
        )}
        {!loading && !featuredSlide && (
          <SessionDismissible id={NUDGE_IDS.HOME_HERO_EMPTY}>
            <HeroEmpty hasFollowing={followingRows.length > 0} />
          </SessionDismissible>
        )}
      </div>

      {/* ── LAYOUT — sidebars below hero; pin after scroll past hero ── */}
      <div className={`hm-layout${sidebarsPinned ? " hm-layout--pinned" : ""}`}>
        <aside className="hm-left-panel" aria-label="Creator windows">
          <div className="hm-lp-head">
            <div className="hm-lp-title">Your creators</div>
            <span className="hm-lp-hand">all your open windows</span>
          </div>

          {loading && <div className="hm-lp-loading">Loading your creators…</div>}

          {!loading && urgentSidebarRows.length > 0 && (
            <>
              <button
                type="button"
                className="hm-lp-label hm-lp-label--closing hm-lp-label--toggle"
                onClick={() => setClosingExpanded((v) => !v)}
                aria-expanded={closingExpanded}
              >
                <span className="hm-lp-label-dot" />Closing soon
                <span className="hm-lp-label-count">{urgentSidebarRows.length}</span>
                <IconChevron className={`hm-lp-chevron${closingExpanded ? " open" : ""}`} />
              </button>
              {closingExpanded && urgentSidebarRows.map(({ creator, drop }) => (
                <CreatorRow key={drop.id} creator={creator} drop={drop} urgent />
              ))}
            </>
          )}

          {!loading && openSidebarRows.length > 0 && (
            <>
              <button
                type="button"
                className="hm-lp-label hm-lp-label--open hm-lp-label--toggle"
                onClick={() => setOpenExpanded((v) => !v)}
                aria-expanded={openExpanded}
              >
                <span className="hm-lp-label-dot" />Open now
                <span className="hm-lp-label-count">{openSidebarRows.length}</span>
                <IconChevron className={`hm-lp-chevron${openExpanded ? " open" : ""}`} />
              </button>
              {openExpanded && openSidebarRows.map(({ creator, drop }) => (
                <CreatorRow key={drop.id} creator={creator} drop={drop} />
              ))}
            </>
          )}

          {!loading && followingRows.length === 0 && (
            <div className="hm-lp-empty">
              You're not following anyone yet.
            </div>
          )}

          {!loading && seenRows.length > 0 && (
            <>
              <div className="hm-lp-label hm-lp-label--seen">
                <span className="hm-lp-label-dot" />Up to date
              </div>
              {seenRows.map(({ creator, drop }) => (
                <SeenRow key={creator.id} creator={creator} drop={drop} />
              ))}
            </>
          )}

          <div className="hm-lp-divider" />
          <div className="hm-lp-find">
            <Link to="/gallery">+ Discover more creators →</Link>
          </div>
        </aside>

        {/* FEED */}
        <div className="hm-feed">
          <div className="hm-greeting">
            <div>
              <div className="hm-greeting-h">{greeting}, <em>{myDisplayName?.split(" ")[0] || "friend"}.</em></div>
              <span className="hm-greeting-hand">
                {urgentFeedRows.length + openFeedRows.length > 0
                  ? `${urgentFeedRows.length + openFeedRows.length} window${urgentFeedRows.length + openFeedRows.length === 1 ? "" : "s"} open — don't miss ${urgentFeedRows.length + openFeedRows.length === 1 ? "it" : "them"}`
                  : "nothing open right now — check the Gallery"}
                {newSinceLastVisit > 0 && ` · ${newSinceLastVisit} new since you were last here`}
              </span>
            </div>
            {urgentFeedRows[0] && (
              <div className="hm-greeting-note">
                <div className="hm-gu-time">{viewsLeft(urgentFeedRows[0].drop)} left</div>
                <div className="hm-gu-sub">{urgentFeedRows[0].creator.displayName}'s window</div>
              </div>
            )}
          </div>

          {creatorRecap && <CreatorRecapCard recap={creatorRecap} />}

          {(urgentFeedRows.length > 0 || openFeedRows.length > 0) && (
            <div className="hm-sec-label">
              <span className="hm-sl-h">Also <em>open</em></span>
              <span className="hm-sl-hand">your windows are running</span>
              <div className="hm-sl-line" />
            </div>
          )}

          {openFeedList.map(({ creator, drop }, i) => {
            const showPill =
              (i + 1) % 9 === 0 || (openFeedList.length < 8 && i === openFeedList.length - 1);
            return (
              <Fragment key={drop.id}>
                <PostCard
                  creator={creator}
                  drop={drop}
                  urgent={viewsLeft(drop) <= 15}
                  access={getDropAccess(drop, purchasedDropIds, subscribedCreatorIds)}
                  isNew={previousVisitMillis > 0 && toMillis(drop.createdAt) > previousVisitMillis}
                />
                {showPill && <FeedReferralPill />}
              </Fragment>
            );
          })}

          {openFeedList.length === 0 && <FeedReferralPill />}

          {closingFastUnfollowed.length > 0 && (
            <SessionDismissible id={NUDGE_IDS.HOME_CLOSING_FAST} tone="dark">
              <ClosingFastInterrupt items={closingFastUnfollowed} />
            </SessionDismissible>
          )}

          {seenRows.map(({ creator, drop }) =>
            drop ? (
              <ClosedPostCard
                key={drop.id}
                creator={creator}
                drop={drop}
                firstViewRank={firstViewRanks[drop.id]}
              />
            ) : null
          )}

          <SessionDismissible id={NUDGE_IDS.HOME_NICHE_BROWSE}>
            <NicheBrowseInterrupt />
          </SessionDismissible>
        </div>

        {/* RIGHT SIDEBAR */}
        <aside className="hm-right-panel" aria-label="Platform stats">
          <div className="hm-live-card">
            <div className="hm-lc-top">
              <div className="hm-lc-dot" /><div className="hm-lc-badge">Right now</div>
            </div>
            <div className="hm-lc-num">{totalCreators ?? "—"}</div>
            <div className="hm-lc-sub">Canadian creators live</div>
          </div>

          <SessionDismissible id={NUDGE_IDS.HOME_EXPLORE_GALLERY}>
            <div className="hm-gallery-card">
              <div className="hm-gc-title">Explore the Gallery</div>
              <span className="hm-gc-hand">you might be missing someone</span>
              <div className="hm-gc-sub">
                {totalCreators ?? "Several"} Canadian creators on the platform. You follow {followingRows.length}. There's more to find.
              </div>
              <Link to="/gallery" className="hm-gc-btn">Go to Gallery →</Link>
            </div>
          </SessionDismissible>
        </aside>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SUBCOMPONENTS
───────────────────────────────────────────── */

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/**
 * The creator-only "while you were gone" recap. Bounded, honest,
 * three real numbers max — not a dashboard, just enough to decide
 * whether the full Dashboard is worth a visit right now. Every number
 * here comes from useCreatorRecap, which only ever reads real
 * Firestore data — never simulated or padded.
 */
function CreatorRecapCard({ recap }) {
  const awayLabel = recap.hoursAway
    ? `${recap.hoursAway}h`
    : `${recap.minutesAway}m`;

  const stats = [];
  if (recap.newPaidUnlocks > 0) {
    stats.push({ num: recap.newPaidUnlocks, label: recap.newPaidUnlocks === 1 ? "paid unlock" : "paid unlocks" });
  }
  stats.push({ num: recap.newViews, label: recap.newViews === 1 ? "new view" : "new views" });
  if (recap.newlyLocked > 0) {
    stats.push({ num: recap.newlyLocked, label: recap.newlyLocked === 1 ? "drop locked" : "drops locked" });
  }
  stats.push({ num: recap.newFollowers, label: recap.newFollowers === 1 ? "new follower" : "new followers" });

  // Keep the card a genuine glance, not a mini-dashboard — cap at 3
  // stats even if more would technically qualify. Paid unlocks and
  // newly-locked drops (when present) are the most meaningful, so
  // they're prioritized ahead of the always-present views/followers.
  const visibleStats = stats.slice(0, 3);

  return (
    <div className="hm-recap-card">
      <div className="hm-recap-label">
        <span className="hm-recap-dot" />
        While you were gone · {awayLabel}
      </div>
      <div className="hm-recap-stats">
        {visibleStats.map((s) => (
          <div className="hm-recap-stat" key={s.label}>
            <div className="hm-recap-num">{s.num}</div>
            <div className="hm-recap-stat-label">{s.label}</div>
          </div>
        ))}
      </div>
      <span className="hm-recap-hand">a quick look before the full picture</span>
      <Link to="/dashboard" className="hm-recap-btn">View full dashboard →</Link>
    </div>
  );
}

function HeroEmpty({ hasFollowing }) {
  return (
    <div className="hm-hero hm-hero-empty">
      <div className="hm-hero-empty-body">
        <h1 className="hm-hero-title">
          {hasFollowing
            ? "Nothing new from your people right now —"
            : "It's quiet in here —"}
          <br />
          <em>{hasFollowing ? "check the Gallery while you wait." : "because you haven't picked anyone yet."}</em>
        </h1>
        <Link to="/gallery" className="hm-btn-hero-main">Go to Gallery</Link>
      </div>
    </div>
  );
}

function CreatorRow({ creator, drop, urgent }) {
  const left = viewsLeft(drop);
  const pct = pctViewed(drop);
  return (
    <>
      <div className={`hm-cr-row ${urgent ? "hm-urgent" : "hm-open"}`}>
        <ProfileAvatarLink
          user={creator}
          src={creator.avatarUrl || creator.avatar}
          alt={creator.displayName}
          className="hm-cr-av-outer"
          imgClassName="hm-cr-av"
        />
        <div className="hm-cr-info-link">
          <div className="hm-cr-info">
            <ProfileNameLink user={creator} className="hm-cr-name">
              {creator.displayName}
            </ProfileNameLink>
            <Link to={`/drop/${drop.id}`} className="hm-cr-niche">
              <NicheInline
                niche={drop.niche}
                suffix={` · ${drop.viewCount || 0}/${drop.maxViews || 100}${urgent ? " ⚡" : ""}`}
              />
            </Link>
          </div>
        </div>
        <Link to={`/drop/${drop.id}`} className="hm-cr-right">
          <div className={`hm-cr-timer ${urgent ? "hm-ct-r" : "hm-ct-g"}`}>{left}</div>
          <div className="hm-cr-count">left</div>
        </Link>
      </div>
      <div className="hm-cr-timebar">
        <div className={`hm-ctb-fill ${urgent ? "hm-ctb-r" : "hm-ctb-g"}`} style={{ width: `${pct}%` }} />
      </div>
    </>
  );
}

function SeenRow({ creator, drop }) {
  const when = drop ? timeSince(drop.createdAt) : "no drops yet";
  const detailTo = drop ? `/drop/${drop.id}` : `/creator/${creator.handle || creator.id}`;

  return (
    <div className="hm-seen-row">
      <ProfileAvatarLink
        user={creator}
        src={creator.avatarUrl || creator.avatar}
        alt={creator.displayName}
        className="hm-seen-av-link"
        imgClassName="hm-seen-av"
      />
      <div className="hm-seen-info-link">
        <div className="hm-seen-info">
          <ProfileNameLink user={creator} className="hm-seen-name">
            {creator.displayName}
          </ProfileNameLink>
          <Link to={detailTo} className="hm-seen-when">
            <NicheInline niche={drop?.niche ?? "independent"} suffix={` · ${when}`} />
          </Link>
        </div>
        {drop && (
          <Link to={detailTo} className="hm-seen-time">
            {when.replace(" ago", "")}
          </Link>
        )}
      </div>
    </div>
  );
}

function PostCard({ creator, drop, urgent, access, isNew }) {
  const left = viewsLeft(drop);
  const pct = pctViewed(drop);
  const type = access?.type || "free";
  const isBlurred = type === "sub-locked";

  return (
    <div className="hm-post">
      <div className={`hm-post-win ${urgent ? "hm-pw-urgent" : "hm-pw-open"}`}>
        {urgent ? (
          <span className="hm-pwt-r"><IconWarning />Window closing — {left} left</span>
        ) : (
          <span className="hm-pwt-m">Open · {left} views left</span>
        )}
      </div>
      <div className="hm-post-accent" />
      <div className="hm-post-head">
        <ProfileAvatarLink
          user={creator}
          src={creator.avatarUrl || creator.avatar}
          alt={creator.displayName}
          className="hm-post-av-link"
          imgClassName="hm-post-av"
        />
        <div className="hm-post-meta">
          <ProfileNameLink user={creator} className="hm-post-name">
            {creator.displayName}
          </ProfileNameLink>
          <div className="hm-post-sub">
            <span className="hm-post-handle">@{creator.handle}</span>
            <NicheBadge niche={drop.niche} />
            <span className="hm-post-time">· {timeSince(drop.createdAt)}</span>
            {isNew && <span className="hm-new-tag">New</span>}
          </div>
        </div>
        <Link to={`/drop/${drop.id}`} className={`hm-scar-pill ${urgent ? "hm-sp-warn" : "hm-sp-open"}`}>
          <span className="hm-sp-dot" />{drop.viewCount || 0}/{drop.maxViews || 100}{urgent ? " ⚡" : ""}
        </Link>
      </div>

      <Link to={`/drop/${drop.id}`} className="hm-thumb">
        <img
          src={drop.thumbnailUrl || "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1000&q=85"}
          alt=""
          className={isBlurred ? "hm-thumb-blurred" : ""}
        />
        <div className="hm-thumb-shade" />

        {type === "free" && <span className="hm-access-badge hm-access-free">Free</span>}
        {(type === "paid-locked" || type === "paid-unlocked") && (
          <span className="hm-access-badge hm-access-paid">${formatPrice(drop.price)}</span>
        )}

        {type === "sub-locked" && (
          <div className="hm-sub-overlay">
            <IconLock />
            <div className="hm-sub-overlay-text">Subscribers Only</div>
          </div>
        )}

        <div className="hm-view-chip">
          <div className={`hm-vc-num ${urgent ? "hm-warn" : "hm-open"}`}>{drop.viewCount || 0}</div>
          <div className="hm-vc-text"><strong>of {drop.maxViews || 100}</strong>{urgent ? "closing ⚡" : "open"}</div>
        </div>
      </Link>

      <div className="hm-scar-bar"><div className={`hm-scar-fill hm-loaded ${urgent ? "hm-sf-g" : "hm-sf-n"}`} style={{ width: `${pct}%` }} /></div>
      <div className="hm-post-body">
        <div className="hm-post-title">{drop.title}</div>
        {drop.description && <div className="hm-post-excerpt">{drop.description}</div>}
      </div>
      <div className="hm-post-foot">
        <div className="hm-post-acts">
          <button className="hm-da"><IconHeart />{drop.likesCount || 0}</button>
          <button className="hm-da"><IconBookmark /></button>
          <button className="hm-da"><IconShare /></button>
        </div>

        {type === "free" && (
          <Link to={`/drop/${drop.id}`} className="hm-unlock hm-u-ghost">Watch Now</Link>
        )}
        {type === "paid-locked" && (
          <Link to={`/drop/${drop.id}`} className="hm-unlock hm-u-gold">Unlock · ${formatPrice(drop.price)}</Link>
        )}
        {type === "paid-unlocked" && (
          <Link to={`/drop/${drop.id}`} className="hm-unlock hm-u-ghost">Watch Now</Link>
        )}
        {type === "sub-locked" && (
          <Link to={`/creator/${creator.handle || creator.id}`} className="hm-unlock hm-u-gold">
            Subscribe to {creator.displayName?.split(" ")[0]} · ${formatPrice(drop.subscriptionPrice || creator.subscriptionPrice)}/mo
          </Link>
        )}
        {type === "sub-unlocked" && (
          <Link to={`/drop/${drop.id}`} className="hm-unlock hm-u-ghost">Watch Now</Link>
        )}
      </div>
    </div>
  );
}

function ClosedPostCard({ creator, drop, firstViewRank }) {
  const wasFirstViewer = typeof firstViewRank === "number";
  const nicheMeta = niche(drop);

  return (
    <div className="hm-post hm-post-closed">
      <div className="hm-post-win hm-pw-closed">
        <span className="hm-pwt-m">
          {wasFirstViewer ? "You had early access" : "Window closed"} · {timeSince(drop.createdAt)}
        </span>
      </div>
      <div className="hm-post-accent hm-post-accent-closed" />
      <div className="hm-post-head">
        <ProfileAvatarLink
          user={creator}
          src={creator.avatarUrl || creator.avatar}
          alt={creator.displayName}
          className={`hm-post-av-link${wasFirstViewer ? "" : " hm-post-av--muted"}`}
          imgClassName="hm-post-av"
        />
        <div className="hm-post-meta">
          <ProfileNameLink user={creator} className="hm-post-name">
            {creator.displayName}
          </ProfileNameLink>
          <div className="hm-post-sub">
            <NicheBadge niche={nicheMeta.key} label={nicheMeta.label} />
          </div>
        </div>
        {wasFirstViewer ? (
          <Link to={`/drop/${drop.id}`} className="hm-scar-pill hm-sp-firstview">
            You were here · #{firstViewRank} of {drop.maxViews || 100}
          </Link>
        ) : (
          <Link to={`/drop/${drop.id}`} className="hm-scar-pill hm-sp-closed">
            Closed
          </Link>
        )}
      </div>
      <Link to={`/drop/${drop.id}`} className="hm-thumb">
        <img
          src={drop.thumbnailUrl || "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1000&q=85"}
          alt=""
          style={wasFirstViewer ? {} : { filter: "grayscale(20%)" }}
        />
        {!wasFirstViewer && (
          <div className="hm-locked-overlay">
            <IconLock />
            <div className="hm-locked-overlay-text">This drop has closed</div>
          </div>
        )}
      </Link>
      <div className="hm-post-body">
        <div className="hm-post-title">{drop.title}</div>
        <div className="hm-post-excerpt">
          {wasFirstViewer
            ? "You were one of the first 100 viewers — you keep permanent access."
            : "This drop's viewing window has closed permanently."}
        </div>
      </div>
      <div className="hm-post-foot">
        <div className="hm-post-acts">
          <button className="hm-da"><IconHeart />{drop.likesCount || 0}</button>
        </div>
        {wasFirstViewer ? (
          <Link to={`/drop/${drop.id}`} className="hm-unlock hm-u-ghost">Watch Now</Link>
        ) : (
          <span className="hm-closed-note">This drop has closed</span>
        )}
      </div>
    </div>
  );
}

function ClosingFastInterrupt({ items }) {
  return (
    <div className="hm-int-closing">
      <div className="hm-ic-header">
        <div className="hm-ic-dot" />
        <span className="hm-ic-label">Closing fast across the platform</span>
        <span className="hm-ic-hand">— not from who you follow</span>
      </div>
      <div className="hm-ic-scroll">
        {items.map(({ drop, creator }) => (
          <Link key={drop.id} to={`/drop/${drop.id}`} className="hm-ic-card">
            <div className="hm-icc-thumb">
              <img src={drop.thumbnailUrl || "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&q=85"} alt="" />
              <div className="hm-icc-shade" />
              <div className="hm-icc-pill">{drop.viewCount || 0}/{drop.maxViews || 100} ⚡</div>
            </div>
            <div className="hm-icc-body">
              <div className="hm-icc-creator">{creator.displayName}</div>
              <div className="hm-icc-title">{drop.title}</div>
            </div>
          </Link>
        ))}
      </div>
      <Link to="/gallery" className="hm-ic-link">See all closing drops in the Gallery →</Link>
    </div>
  );
}

function NicheBrowseInterrupt() {
  return (
    <div className="hm-int-niche">
      <div className="hm-in-header">
        <div><span className="hm-in-title">Explore other niches</span><span className="hm-in-hand">find something new</span></div>
        <Link to="/gallery" className="hm-in-link">See all →</Link>
      </div>
      <div className="hm-in-grid">
        {NICHE_BROWSE_LIST.map((n) => (
          <NicheBrowseTile
            key={n.key}
            nicheKey={n.key}
            label={n.label}
            to={`/gallery?niche=${n.key}`}
          />
        ))}
      </div>
    </div>
  );
}